import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Banner4 from '../components/sections/Banner4';
import { caseStudies } from '../data/caseStudies';
import '../styles/glass.css';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const ProcessCard = ({ number, title, description, color = 'primary' }) => {
  return (
    <motion.div 
      className="group cursor-pointer relative"
      initial="initial"
      animate="animate"
      variants={fadeInUp}
    >
      <div className={`absolute inset-0 bg-gradient-to-br from-${color}/20 to-${color}/5 rounded-3xl transform group-hover:scale-[1.02] transition-all duration-500 ease-out opacity-0 group-hover:opacity-100`} />
      <div className="relative glass-effect rounded-3xl p-8">
        <div className={`absolute -top-8 -right-8 w-24 h-24 bg-${color} rounded-full opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500`} />
        <div className="flex items-start gap-6">
          <div className={`w-12 h-12 bg-${color}/20 rounded-2xl p-4 flex items-center justify-center text-${color} font-bold text-xl`}>
            {number}
          </div>
          <div className="space-y-3">
            <h3 className="text-xl font-bold">{title}</h3>
            <p className="text-white/70 leading-relaxed">{description}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const StatCard = ({ value, label, description }) => (
  <motion.div 
    className="group cursor-pointer relative"
    initial="initial"
    animate="animate"
    variants={fadeInUp}
  >
    <div className="relative glass-effect rounded-3xl p-8">
      <div className="text-4xl font-bold mb-2 bg-gradient-to-r from-white via-white/90 to-white/80 text-transparent bg-clip-text">
        {value}
      </div>
      <div className="text-lg font-medium text-white/80 mb-1">
        {label}
      </div>
      <p className="text-sm text-white/60">
        {description}
      </p>
    </div>
  </motion.div>
);

const SingleCaseStudy = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const caseStudy = caseStudies[caseId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!caseStudy) {
    navigate('/case-studies');
    return null;
  }

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      variants={stagger}
      className="min-h-screen bg-black text-white"
    >
      {/* Hero Section */}
      <motion.section 
        variants={fadeInUp}
        className="relative pt-32 pb-24 overflow-hidden"
      >
        <div className="container mx-auto px-4 relative">
          <motion.div 
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {caseStudy.title}
            </h1>
            <p className="text-xl text-white/60 mb-12">
              {caseStudy.subtitle}
            </p>
            {caseStudy.video && (
              <div className="relative mb-12">
                <div className="w-full aspect-video rounded-2xl overflow-hidden">
                  <iframe 
                    className="w-full h-full"
                    src={caseStudy.video} 
                    title={`${caseStudy.title} Case Study Video`}
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                  />
                </div>
              </div>
            )}

          </motion.div>
        </div>
      </motion.section>

      {/* Stats Grid */}
      <motion.section 
        variants={fadeInUp}
        className="py-24 relative"
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caseStudy.stats.map((stat, index) => (
              <StatCard key={index} {...stat} />
            ))}
          </div>
        </div>
      </motion.section>

      {/* Process Section */}
      <motion.section 
        variants={fadeInUp}
        className="py-24 relative"
      >
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Challenges */}
            <div>
              <div className="inline-block bg-primary/20 text-primary px-6 py-2 rounded-full text-sm font-medium mb-8">
                CHALLENGES
              </div>
              <div className="space-y-8">
                {caseStudy.challenges.map((challenge, index) => (
                  <ProcessCard
                    key={index}
                    number={`0${index + 1}`}
                    title={challenge.title}
                    description={challenge.description}
                  />
                ))}
              </div>
            </div>

            {/* Solutions */}
            <div>
              <div className="inline-block bg-primary/20 text-primary px-6 py-2 rounded-full text-sm font-medium mb-8">
                OUR SOLUTION
              </div>
              <div className="space-y-8">
                {caseStudy.workDone.map((work, index) => (
                  <ProcessCard
                    key={index}
                    number={`0${index + 1}`}
                    title={work.title}
                    description={work.description}
                    color="green-500"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Testimonial */}
      <motion.section 
        variants={fadeInUp}
        className="py-24 relative"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-12">
              <div className="text-2xl italic text-white/80 mb-8">
                "{caseStudy.testimonial.quote}"
              </div>
              <div>
                <div className="font-semibold text-lg">{caseStudy.testimonial.author}</div>
                <div className="text-primary">{caseStudy.testimonial.position}</div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      <Banner4 />
    </motion.div>
  );
};

export default SingleCaseStudy;
